import React, { useState, useEffect } from 'react';

function HotspotForm({ type, initialData, onSave, onCancel, position, scenes, metadata }) {
    const isEdit = !!initialData;
    const isInfo = type === 'info';

    const [formData, setFormData] = useState({
        name: '',
        infoSpotType: 'audioTranscript',
        audioFolderMale: metadata?.infoVoFolderMale || metadata?.infoVoFolder || '',
        audioFolderFemale: metadata?.infoVoFolderFemale || '',
        audioFile: '',
        audioFileFemale: '',
        title: '',
        primaryContent: '',
        hotspotRoom: 'main',
        hotspotProduct: '',
        hotspotType: 'info',
        breadCrum: '',
        productHeadline: '',
        primaryImage: '',
        icon: '',
        filterLabels: '',
        // Transition fields
        linkTo: 0,
    });

    const [audioFilesListMale, setAudioFilesListMale] = useState([]);
    const [audioFilesListFemale, setAudioFilesListFemale] = useState([]);

    // Fetch audio files whenever audio folders change
    useEffect(() => {
        if (formData.infoSpotType === 'audioTranscript') {
            if (formData.audioFolderMale) {
                fetch(`http://localhost:3002/api/audio-files?folder=${encodeURIComponent(formData.audioFolderMale)}`)
                    .then(res => res.json())
                    .then(data => {
                        if (Array.isArray(data)) setAudioFilesListMale(data);
                    })
                    .catch(err => console.error(err));
            }
            if (formData.audioFolderFemale) {
                fetch(`http://localhost:3002/api/audio-files?folder=${encodeURIComponent(formData.audioFolderFemale)}`)
                    .then(res => res.json())
                    .then(data => {
                        if (Array.isArray(data)) setAudioFilesListFemale(data);
                    })
                    .catch(err => console.error(err));
            }
        }
    }, [formData.infoSpotType, formData.audioFolderMale, formData.audioFolderFemale]);

    useEffect(() => {
        if (initialData) {
            if (isInfo && initialData.solutionModelData) {
                setFormData({
                    name: initialData.solutionModelData.infoSpot || '',
                    infoSpotType: initialData.solutionModelData.infoSpotType || 'audioTranscript',
                    audioFolderMale: initialData.solutionModelData.audioFolderMale || initialData.solutionModelData.audioFolder || '',
                    audioFolderFemale: initialData.solutionModelData.audioFolderFemale || '',
                    audioFile: initialData.solutionModelData.audioFile || '',
                    audioFileFemale: initialData.solutionModelData.audioFileFemale || '',
                    title: initialData.solutionModelData.title || '',
                    primaryContent: initialData.solutionModelData.primaryContent || '',
                    hotspotRoom: initialData.solutionModelData.hotspotRoom || 'main',
                    hotspotProduct: initialData.solutionModelData.hotspotProduct || '',
                    hotspotType: initialData.solutionModelData.hotspotType || 'info',
                    breadCrum: initialData.solutionModelData.breadCrum || '',
                    productHeadline: initialData.solutionModelData.productHeadline || '',
                    primaryImage: initialData.solutionModelData.primaryImage || '',
                    icon: initialData.solutionModelData.icon || '',
                    filterLabels: initialData.solutionModelData.filterLabels ? initialData.solutionModelData.filterLabels.join(', ') : '',
                    linkTo: 0,
                });
            } else if (!isInfo) {
                setFormData({
                    ...formData,
                    name: initialData.name || '',
                    linkTo: initialData.linkTo ?? 0,
                    hotspotRoom: initialData.hotspotRoom || 'home_automation',
                });
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [initialData, isInfo]);

    const handleChange = (field, value) => {
        setFormData((prev) => ({ ...prev, [field]: value }));
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        if (isInfo) {
            onSave({
                name: formData.name,
                infoSpotType: formData.infoSpotType,
                audioFolderMale: formData.audioFolderMale,
                audioFolderFemale: formData.audioFolderFemale,
                audioFile: formData.audioFile,
                audioFileFemale: formData.audioFileFemale,
                title: formData.title,
                primaryContent: formData.primaryContent,
                hotspotRoom: formData.hotspotRoom,
                hotspotProduct: formData.hotspotProduct,
                hotspotType: formData.hotspotType,
                breadCrum: formData.breadCrum,
                productHeadline: formData.productHeadline,
                primaryImage: formData.primaryImage,
                icon: formData.icon,
                filterLabels: formData.filterLabels ? formData.filterLabels.split(',').map(s => s.trim()) : [],
                position: position || (initialData && initialData.position),
            });
        } else {
            const linkedScene = scenes?.find(s => s.sceneIndex === Number(formData.linkTo));
            onSave({
                name: formData.name || linkedScene?.name || 'Link',
                linkTo: Number(formData.linkTo),
                hotspotRoom: formData.hotspotRoom,
                position: position || (initialData && initialData.position),
            });
        }
    };

    return (
        <div className="admin-hotspot-form-overlay">
            <form className="admin-hotspot-form" onSubmit={handleSubmit}>
                <div className="admin-form-header">
                    <h3>{isEdit ? 'Edit' : 'New'} {isInfo ? 'Info Hotspot' : 'Transition Node'}</h3>
                    <button type="button" className="admin-form-close" onClick={onCancel}>×</button>
                </div>

                {position && (
                    <div className="admin-form-position">
                        📍 Position: ({position.x.toFixed(1)}, {position.y.toFixed(1)}, {position.z.toFixed(1)})
                    </div>
                )}

                <div className="admin-form-fields">
                    <div className="admin-form-group">
                        <label>Name / Label</label>
                        <input
                            type="text"
                            value={formData.name}
                            onChange={(e) => handleChange('name', e.target.value)}
                            placeholder={isInfo ? 'e.g. Architecture & Gates' : 'e.g. Entrance'}
                            required
                        />
                    </div>

                    {isInfo && (
                        <>
                            <div className="admin-form-group">
                                <label>Info Type</label>
                                <select
                                    value={formData.infoSpotType}
                                    onChange={(e) => handleChange('infoSpotType', e.target.value)}
                                >
                                    <option value="audioTranscript">Audio Transcript</option>
                                    <option value="solutionModal">Solution Modal</option>
                                </select>
                            </div>

                            {formData.infoSpotType === 'audioTranscript' && (
                                <>
                                    <div className="admin-form-group">
                                        <label>👨 Male VO File Layout (no extension)</label>
                                        <input
                                            list="audio-files-male"
                                            value={formData.audioFile}
                                            onChange={(e) => handleChange('audioFile', e.target.value)}
                                            placeholder="Type to search male audio files..."
                                            autoComplete="off"
                                        />
                                        <datalist id="audio-files-male">
                                            {audioFilesListMale.map(file => (
                                                <option key={file} value={file} />
                                            ))}
                                        </datalist>
                                    </div>

                                    <div className="admin-form-group" style={{ marginTop: '20px' }}>
                                        <label>👩 Female VO File Layout (no extension)</label>
                                        <input
                                            list="audio-files-female"
                                            value={formData.audioFileFemale}
                                            onChange={(e) => handleChange('audioFileFemale', e.target.value)}
                                            placeholder="Type to search female audio files..."
                                            autoComplete="off"
                                        />
                                        <datalist id="audio-files-female">
                                            {audioFilesListFemale.map(file => (
                                                <option key={file} value={file} />
                                            ))}
                                        </datalist>
                                    </div>
                                </>
                            )}

                            {formData.infoSpotType === 'solutionModal' && (
                                <>
                                    <div className="admin-form-group">
                                        <label>Modal Title</label>
                                        <input
                                            type="text"
                                            value={formData.title}
                                            onChange={(e) => handleChange('title', e.target.value)}
                                            placeholder="e.g. Access Control with Secure Door Lock Solutions"
                                        />
                                    </div>
                                    <div className="admin-form-group">
                                        <label>Description</label>
                                        <textarea
                                            value={formData.primaryContent}
                                            onChange={(e) => handleChange('primaryContent', e.target.value)}
                                            placeholder="Description text..."
                                            rows={2}
                                        />
                                    </div>
                                    <div className="admin-form-group">
                                        <label>Breadcrumb</label>
                                        <input
                                            type="text"
                                            value={formData.breadCrum}
                                            onChange={(e) => handleChange('breadCrum', e.target.value)}
                                            placeholder="e.g. HOMES / HOME SECURITY"
                                        />
                                    </div>
                                    <div className="admin-form-group">
                                        <label>Product Headline</label>
                                        <input
                                            type="text"
                                            value={formData.productHeadline}
                                            onChange={(e) => handleChange('productHeadline', e.target.value)}
                                            placeholder="e.g. Learn more about our solutions..."
                                        />
                                    </div>
                                    <div className="admin-form-group">
                                        <label>Primary Image URL (or path)</label>
                                        <input
                                            type="text"
                                            value={formData.primaryImage}
                                            onChange={(e) => handleChange('primaryImage', e.target.value)}
                                            placeholder="/images/hero.webp"
                                        />
                                    </div>
                                    <div className="admin-form-group">
                                        <label>Filter Labels (comma separated)</label>
                                        <input
                                            type="text"
                                            value={formData.filterLabels}
                                            onChange={(e) => handleChange('filterLabels', e.target.value)}
                                            placeholder="Z-Wave, Bluetooth, Thread"
                                        />
                                    </div>
                                </>
                            )}

                            <div className="admin-form-row">
                                <div className="admin-form-group">
                                    <label>Hotspot Type</label>
                                    <select
                                        value={formData.hotspotType}
                                        onChange={(e) => handleChange('hotspotType', e.target.value)}
                                    >
                                        <option value="info">General Info</option>
                                        <option value="product">Product</option>
                                    </select>
                                </div>
                                <div className="admin-form-group">
                                    <label>Room Category</label>
                                    <input
                                        type="text"
                                        value={formData.hotspotRoom}
                                        onChange={(e) => handleChange('hotspotRoom', e.target.value)}
                                        placeholder="e.g. home_security"
                                    />
                                </div>
                            </div>
                            <div className="admin-form-group">
                                <label>Hotspot Product ID (optional)</label>
                                <input
                                    type="text"
                                    value={formData.hotspotProduct}
                                    onChange={(e) => handleChange('hotspotProduct', e.target.value)}
                                    placeholder="e.g. door_lock"
                                />
                            </div>
                        </>
                    )}

                    {!isInfo && (
                        <>
                            <div className="admin-form-group">
                                <label>Navigate To Scene</label>
                                <select
                                    value={formData.linkTo}
                                    onChange={(e) => handleChange('linkTo', e.target.value)}
                                >
                                    {scenes && scenes.map((scene) => (
                                        <option key={scene.sceneIndex} value={scene.sceneIndex}>
                                            {scene.sceneIndex}. {scene.name}
                                        </option>
                                    ))}
                                </select>
                            </div>
                        </>
                    )}
                </div>

                <div className="admin-form-actions">
                    <button type="button" className="admin-btn-secondary" onClick={onCancel}>
                        Cancel
                    </button>
                    <button type="submit" className="admin-btn-primary">
                        {isEdit ? 'Update' : 'Create'} Hotspot
                    </button>
                </div>
            </form>
        </div>
    );
}

export default HotspotForm;
